import { Injectable } from '@angular/core';
import type { FactMinInterface } from '@zaoblako/shared/common/dtos/fact-min.dto';
import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import type { ReportItemsPageInterface } from '@zaoblako/shared/common/dtos/report/report-items-page.dto';
import type { HelpdeskChangeStatusFormInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-change-status-form.dto';
import type { CardDescriptorInterface } from '@zaoblako/shared/common/dtos/card/card-descriptor.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { EntityCardHistoryQueryInterface } from '@zaoblako/shared/common/dtos/history/entity-card-history-query.dto';
import type { HelpdeskTaskPageInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-page.dto';
import type { HelpdeskTaskQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-query.dto';
import type { HelpdeskTaskRescheduleInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-reschedule.dto';
import type { HelpdeskTaskSearchQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-search-query.dto';
import type { HelpdeskTaskStatusFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';
import type { HelpdeskTaskReportItemInterface } from '@zaoblako/shared/modules/helpdesk/dto/report/helpdesk-task-report-item.dto';
import type { HelpdeskTaskReportQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/report/helpdesk-task-report-query.dto';
import type { HelpdeskTaskReportSumsInterface } from '@zaoblako/shared/modules/helpdesk/dto/report/helpdesk-task-report-sums.dto';
import type { ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import type { ReminderFormInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder.form.dto';
import type { HelpdeskTaskRequiredFieldsInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-required-fields.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { CommentaryFilePageInterface } from '@zaoblako/shared/modules/storage/dtos/commentary-file/commentary-file-page.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import type { HelpdeskCreateManyFromTemplateForContragentsFormInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-create-many-from-template-for-contragents-form.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HelpDeskTaskService extends BaseService {
  protected baseUrl = '/helpdesk/task';

  protected numberPrefix = 'HD';

  public getRegistryDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(this.baseUrl);
  }

  public getCardDescriptor(): Observable<CardDescriptorInterface> {
    return this.http.get<CardDescriptorInterface>(`${this.baseUrl}/card-descriptor`);
  }

  public findRegistryPage(
    query?: HelpdeskTaskQueryInterface
  ): Observable<ReportItemsPageInterface<HelpdeskTaskFindInterface>> {
    return this.http.get<ReportItemsPageInterface<HelpdeskTaskFindInterface>>(`${this.baseUrl}/items`, {
      params: query ? this.objectToHttpParams(query) : {}
    });
  }

  public createManyFromTemplateForContragents(
    body: HelpdeskCreateManyFromTemplateForContragentsFormInterface
  ): Observable<HelpdeskTaskFindInterface[]> {
    return this.http.post<HelpdeskTaskFindInterface[]>(`${this.baseUrl}/create-many-from-template-for-contragents`, body);
  }

  /**
   * @deprecated Use {@link HelpDeskTaskService.findWithPagination} method
   *
   * @param [queryParams] Filters and settings
   *
   * @returns "rxjs" observable with HD tasks slice
   */
  public search(queryParams?: HelpdeskTaskSearchQueryInterface): Observable<HelpdeskTaskPageInterface> {
    return this.findWithPagination(
      queryParams
        ? {
            search: queryParams?.search,
            limit: queryParams?.limit,
            offset: queryParams?.offset,
            ignorePeriod: true,
            from: undefined,
            to: undefined,
            select: ['id', 'number', 'responsible', 'status']
          }
        : {
            ignorePeriod: true,
            from: undefined,
            to: undefined,
            select: ['id', 'number', 'responsible', 'status']
          }
    );
  }

  public findWithPagination(queryParams: HelpdeskTaskQueryInterface): Observable<HelpdeskTaskPageInterface> {
    return this.http.get<HelpdeskTaskPageInterface>(`${this.baseUrl}/items`, { params: this.objectToHttpParams(queryParams) });
  }

  public changeStatus(
    id: number,
    body: HelpdeskChangeStatusFormInterface
  ): Observable<HelpdeskTaskStatusFindInterface> {
    return this.http.put<HelpdeskTaskStatusFindInterface>(`${this.baseUrl}/${id}/status`, body);
  }

  public getMembers(taskId: number): Observable<UserFindInterface[]> {
    return this.http.get<UserFindInterface[]>(`${this.baseUrl}/${taskId}/participants`);
  }

  public getStatuses(): Observable<HelpdeskTaskStatusFindInterface[]> {
    return this.http.get<HelpdeskTaskStatusFindInterface[]>(`${this.baseUrl}/status`);
  }

  public getTasksReport(queryParams: HelpdeskTaskReportQueryInterface): Observable<HelpdeskTaskReportItemInterface[]> {
    return this.http.get<HelpdeskTaskReportItemInterface[]>('/helpdesk/report/task', {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getSumsReport(queryParams: HelpdeskTaskReportQueryInterface): Observable<HelpdeskTaskReportSumsInterface> {
    return this.http.get<HelpdeskTaskReportSumsInterface>('/helpdesk/report/task/sums', {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getTasksReportExcel(queryParams: HelpdeskTaskReportQueryInterface): Observable<FileFindInterface> {
    return this.http.get<FileFindInterface>('/helpdesk/report/task/excel', {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getFactTime(id: number): Observable<FactMinInterface> {
    return this.http.get<FactMinInterface>(`${this.baseUrl}/${id}/fact-time`);
  }

  public getHistory(id: number, queryParams: EntityCardHistoryQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/${id}/history`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getFiles(taskId: number): Observable<CommentaryFilePageInterface> {
    return this.http.get<CommentaryFilePageInterface>(`${this.baseUrl}/${taskId}/files`);
  }

  public getRequiredFields(): Observable<HelpdeskTaskRequiredFieldsInterface> {
    return this.http.get<HelpdeskTaskRequiredFieldsInterface>(`${this.baseUrl}/required-fields`);
  }

  public changeRequiredFields(
    body: HelpdeskTaskRequiredFieldsInterface
  ): Observable<HelpdeskTaskRequiredFieldsInterface> {
    return this.http.put<HelpdeskTaskRequiredFieldsInterface>(`${this.baseUrl}/required-fields`, body);
  }

  public toggleFavorite(taskId: number): Observable<HelpdeskTaskFindInterface> {
    return this.http.post<HelpdeskTaskFindInterface>(`${this.baseUrl}/${taskId}/toggle-favorite`, {});
  }

  public reschedule(taskId: number, body: HelpdeskTaskRescheduleInterface): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${taskId}/reschedule`, body);
  }

  public createReminder(taskId: number, params: ReminderFormInterface): Observable<ReminderFindInterface> {
    return this.http.post<ReminderFindInterface>(`${this.baseUrl}/${taskId}/reminder`, params);
  }

  public answerWithAi(taskId: number): Observable<string> {
    return this.http.post(`${this.baseUrl}/${taskId}/answer-with-ai`, {}, { responseType: 'text' });
  }

  public mergeOneInto(id: number, targetId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/merge-into/${targetId}`, {});
  }
}
